import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, MapPin, Loader2 } from "lucide-react";
import { MaskedLines, Reveal, FadeUp } from "@/components/motion";
import { CONTACT, PRODUCTS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", email: "", company: "", product: "", message: "" };

const inputClass =
  "w-full bg-transparent border-b border-black/20 focus:border-ink outline-none py-4 text-ink placeholder:text-black/30 font-light transition-colors duration-300";

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="font-mono text-[10px] tracking-[0.25em] uppercase text-black/45"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        product: form.product || null,
        message: form.message.trim(),
      });
      setSent(true);
      toast.success("Enquiry received. Our trading desk will be in touch.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-44 pb-24 sm:pb-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <FadeUp delay={0.1}>
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand">Contact Us</p>
          </FadeUp>
          <MaskedLines
            className="mt-8 font-display font-medium uppercase tracking-tighter leading-[0.95] text-[12vw] sm:text-6xl lg:text-7xl text-ink"
            delay={0.25}
            lines={[
              "Let's",
              <span key="collab">
                Collaborate<span className="text-brand">.</span>
              </span>,
            ]}
          />
          <FadeUp delay={0.9} className="mt-10 max-w-md">
            <p className="text-black/65 font-light leading-relaxed text-base sm:text-lg">
              Collaborate with us for exciting business opportunities. Tell us about your
              requirement and our trading desk will respond.
            </p>
          </FadeUp>

          <FadeUp delay={1.05} className="mt-14 space-y-8">
            <div className="flex items-start gap-4">
              <MapPin className="w-5 h-5 text-brand mt-0.5" />
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-black/45">Location</p>
                <p data-testid="contact-location" className="mt-2 text-black/75 font-light">{CONTACT.location}</p>
              </div>
            </div>
            <div className="border-t border-black/10 pt-8">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-black/45">Trading Desk</p>
              <p className="mt-2 text-black/55 font-light text-sm leading-relaxed max-w-xs">
                {CONTACT.note}
              </p>
            </div>
          </FadeUp>
        </div>

        <Reveal delay={0.3}>
          <div className="border border-black/10 bg-white p-8 sm:p-12" data-testid="contact-form-panel">
            {sent ? (
              <div className="py-16 text-center" data-testid="contact-success">
                <CheckCircle2 className="w-12 h-12 text-brand mx-auto" />
                <h2 className="mt-8 font-display font-medium uppercase tracking-tight text-3xl text-ink">
                  Enquiry received<span className="text-brand">.</span>
                </h2>
                <p className="mt-4 text-black/60 font-light max-w-sm mx-auto">
                  Thank you for reaching out. Our trading desk will review your enquiry and
                  respond shortly.
                </p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => {
                    setForm(initialForm);
                    setSent(false);
                  }}
                  className="mt-10 font-mono text-[11px] tracking-[0.25em] uppercase text-black/60 hover:text-brand transition-colors duration-300"
                >
                  Send Another Enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                <Field label="Full Name *" htmlFor="contact-name">
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className={inputClass}
                  />
                </Field>
                <div className="grid sm:grid-cols-2 gap-8">
                  <Field label="Email *" htmlFor="contact-email">
                    <input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@company.com"
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Company" htmlFor="contact-company">
                    <input
                      id="contact-company"
                      data-testid="contact-company-input"
                      value={form.company}
                      onChange={update("company")}
                      placeholder="Company name"
                      className={inputClass}
                    />
                  </Field>
                </div>
                <Field label="Product of Interest" htmlFor="contact-product">
                  <select
                    id="contact-product"
                    data-testid="contact-product-select"
                    value={form.product}
                    onChange={update("product")}
                    className={`${inputClass} appearance-none cursor-pointer`}
                  >
                    <option value="" className="bg-white">Select a product</option>
                    {PRODUCTS.map((p) => (
                      <option key={p.code} value={p.name} className="bg-white">
                        {p.name}
                      </option>
                    ))}
                    <option value="Other" className="bg-white">Other / Bespoke Requirement</option>
                  </select>
                </Field>
                <Field label="Message *" htmlFor="contact-message">
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    required
                    rows={4}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us about your requirement — volumes, destination, timeline."
                    className={`${inputClass} resize-none`}
                  />
                </Field>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={loading}
                  className="group w-full inline-flex items-center justify-center gap-2 bg-brand text-white px-8 py-5 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-brand-hover disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
                >
                  {loading ? (
                    <>
                      Sending
                      <Loader2 className="w-4 h-4 animate-spin" />
                    </>
                  ) : (
                    <>
                      Send Enquiry
                      <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
